import { lazy, type ComponentType } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Construction } from 'lucide-react';
import type { Permission } from '@parkline/shared';

import { Layout } from './components/Layout';
import { EmptyState } from './components/EmptyState';
import { RequireAuth, RequirePermission, useAuth } from './lib/auth';
import { landingPath } from './lib/shared';
import { LoginPage } from './pages/LoginPage';
import { NotFoundPage } from './pages/NotFoundPage';

/**
 * Страницы кабинетов пишутся отдельно и появляются в src/pages по мере готовности.
 * import.meta.glob не требует, чтобы файл существовал на момент сборки, поэтому
 * каркас компилируется и работает даже с неполным набором страниц.
 */
const pageModules = import.meta.glob([
  './pages/*.tsx',
  // логин и 404 подключены статически — не дублируем их в ленивые чанки
  '!./pages/LoginPage.tsx',
  '!./pages/NotFoundPage.tsx',
]);

type PageModule = { default?: ComponentType };

function lazyPage(file: string, title: string) {
  return lazy(async (): Promise<{ default: ComponentType }> => {
    const loader = pageModules[`./pages/${file}.tsx`];
    if (!loader) {
      return { default: () => <PagePlaceholder title={title} /> };
    }
    try {
      const module = (await loader()) as PageModule;
      if (!module.default) {
        return { default: () => <PagePlaceholder title={title} reason="нет экспорта по умолчанию" /> };
      }
      return { default: module.default };
    } catch (error) {
      console.error(`Не удалось загрузить страницу ${file}`, error);
      return { default: () => <PagePlaceholder title={title} reason="ошибка загрузки модуля" /> };
    }
  });
}

function PagePlaceholder({ title, reason }: { title: string; reason?: string }) {
  return (
    <EmptyState
      icon={<Construction className="h-10 w-10" aria-hidden />}
      title={`Страница «${title}» ещё не подключена`}
      description={
        reason
          ? `Раздел появится после подключения модуля (${reason}).`
          : 'Раздел появится после подключения соответствующего модуля интерфейса.'
      }
    />
  );
}

interface PageRoute {
  path: string;
  /** Без права раздел доступен всем авторизованным — так открыт раздел справки. */
  permission?: Permission;
  component: ComponentType;
}

const PAGE_ROUTES: readonly PageRoute[] = [
  { path: 'guide', component: lazyPage('GuidePage', 'Как это работает') },
  { path: 'board', permission: 'release:read', component: lazyPage('BoardPage', 'Доска выпуска') },
  { path: 'releases', permission: 'release:read', component: lazyPage('ReleasesPage', 'Выпуски') },
  {
    path: 'releases/:id',
    permission: 'release:read',
    component: lazyPage('ReleaseDetailPage', 'Выпуск'),
  },
  { path: 'shifts', permission: 'shift:read', component: lazyPage('ShiftsPage', 'Смены') },
  {
    path: 'shifts/:id',
    permission: 'shift:read',
    component: lazyPage('ShiftDetailPage', 'Смена'),
  },
  {
    path: 'waybills',
    permission: 'waybill:read',
    component: lazyPage('WaybillsPage', 'Путевые листы'),
  },
  {
    path: 'waybills/:id',
    permission: 'waybill:read',
    component: lazyPage('WaybillDetailPage', 'Путевой лист'),
  },
  {
    path: 'assignments',
    permission: 'assignment:read',
    component: lazyPage('AssignmentsPage', 'Наряды'),
  },
  { path: 'mechanic', permission: 'technical:write', component: lazyPage('MechanicPage', 'Техосмотр') },
  { path: 'medic', permission: 'medical:write', component: lazyPage('MedicPage', 'Медосмотр') },
  { path: 'terminal', permission: 'shift:read', component: lazyPage('TerminalPage', 'Терминал') },
  { path: 'driver', permission: 'assignment:respond', component: lazyPage('DriverPage', 'Моя смена') },
  {
    path: 'directories',
    permission: 'directory:read',
    component: lazyPage('DirectoriesPage', 'Справочники'),
  },
  { path: 'users', permission: 'user:manage', component: lazyPage('UsersPage', 'Пользователи') },
  {
    path: 'integrations',
    permission: 'audit:read',
    component: lazyPage('IntegrationsPage', 'Журнал интеграций'),
  },
  { path: 'audit', permission: 'audit:read', component: lazyPage('AuditPage', 'Аудит') },
];

/**
 * Корень открывает раздел по правам роли. Раньше вёл на доску выпуска, и водитель,
 * медработник и механик сразу после входа получали экран «Недостаточно прав».
 */
function LandingRedirect() {
  const { can } = useAuth();
  return <Navigate to={landingPath(can)} replace />;
}

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route element={<RequireAuth />}>
        <Route element={<Layout />}>
          <Route index element={<LandingRedirect />} />
          {PAGE_ROUTES.map(({ path, permission, component: Page }) => (
            <Route
              key={path}
              path={path}
              element={
                permission ? (
                  <RequirePermission permission={permission}>
                    <Page />
                  </RequirePermission>
                ) : (
                  <Page />
                )
              }
            />
          ))}
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Route>
    </Routes>
  );
}
